import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {assignToCallPool, loadCorporateUsers} from './Api'
import {AssignMode, ICorporateUserOption, userDisplayName} from './CallPoolModel'

export interface IAssignTarget {
  /** Aday havuzundan aktarım */
  candidateIds?: string[]
  /** Başvurular ekranından aktarım */
  applicationIds?: string[]
  /** Pencere başlığında gösterilir: "3 aday", "Ayşe Yılmaz" */
  label: string
}

type Props = {
  target: IAssignTarget | null
  onClose: () => void
  onAssigned: () => void
}

/**
 * Arama havuzuna aktarım: kendine ata, bir kullanıcıya ata ya da firma kullanıcıları
 * arasında rastgele (eşit sayıda) dağıt.
 */
export const AssignCallPoolModal: FC<Props> = ({target, onClose, onAssigned}) => {
  const [users, setUsers] = useState<ICorporateUserOption[]>([])
  const [mode, setMode] = useState<AssignMode>('SELF')
  const [userId, setUserId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!target) return
    setMode('SELF')
    setUserId('')
    loadCorporateUsers()
      .then(({data}) => setUsers(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Firma kullanıcıları yüklenemedi')))
  }, [target])

  const count = (target?.candidateIds?.length ?? 0) + (target?.applicationIds?.length ?? 0)

  const handleAssign = async () => {
    if (!target) return
    const selectedUser = users.find((user) => user.userId === userId)
    setSaving(true)
    try {
      const {data} = await assignToCallPool({
        candidateIds: target.candidateIds,
        applicationIds: target.applicationIds,
        mode,
        assignee: mode === 'USER' && selectedUser ? {userId: selectedUser.userId, name: userDisplayName(selectedUser)} : undefined,
        users:
          mode === 'RANDOM'
            ? users.map((user) => ({userId: user.userId, name: userDisplayName(user)}))
            : undefined,
      })
      const {assigned, skipped} = data.payload
      showSuccessToast(
        `${assigned} aday arama havuzuna aktarıldı${skipped > 0 ? `, ${skipped} aday zaten arama listesinde olduğu için atlandı` : ''}`
      )
      onAssigned()
      onClose()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Aktarım yapılamadı'))
    } finally {
      setSaving(false)
    }
  }

  const canSubmit = mode !== 'USER' || !!userId
  const option = (value: AssignMode, title: string, description: string) => (
    <label
      className={`d-flex align-items-start gap-3 border rounded p-4 mb-3 cursor-pointer ${
        mode === value ? 'border-primary bg-light-primary' : 'border-gray-300'
      }`}
    >
      <input
        type='radio'
        className='form-check-input mt-1'
        name='assign-mode'
        checked={mode === value}
        onChange={() => setMode(value)}
      />
      <span className='d-flex flex-column'>
        <span className='fw-bold text-gray-800'>{title}</span>
        <span className='text-muted fs-7'>{description}</span>
      </span>
    </label>
  )

  return (
    <Modal show={!!target} onHide={onClose} dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>Arama Havuzuna Aktar · {target?.label}</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        {option('SELF', 'Bana ata', 'Adaylar senin arama listene eklenir.')}
        {option('USER', 'Bir kullanıcıya ata', 'Adaylar seçtiğin kullanıcının arama listesine eklenir.')}
        {mode === 'USER' && (
          <select className='form-select mb-4' value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value=''>Kullanıcı seçiniz</option>
            {users.map((user) => (
              <option key={user.userId} value={user.userId}>
                {userDisplayName(user)} · {user.email}
              </option>
            ))}
          </select>
        )}
        {option(
          'RANDOM',
          'Rastgele dağıt',
          users.length > 0
            ? `${count} aday firmanın ${users.length} kullanıcısı arasında eşit sayıda paylaştırılır.`
            : 'Adaylar firmanın kullanıcıları arasında eşit sayıda paylaştırılır.'
        )}
        <div className='text-muted fs-7 mt-2'>Arama listesinde zaten bekleyen adaylar tekrar eklenmez.</div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          İptal
        </button>
        <button type='button' className='btn btn-primary' disabled={!canSubmit || saving} onClick={handleAssign}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : 'Aktar'}
        </button>
      </div>
    </Modal>
  )
}
